'use client'

import { type AxiosError } from 'axios'
import useSWR from 'swr'

import {
  type AdServerData,
  GkAirApiFactory,
  type GkAirApiGetGkAirRecommendationsWebRequest,
  type GkAirProxyServiceWebResponse,
  ProdrecApiFactory,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import {
  AD_SERVER_VISIBLE_EVENT_TYPE,
  adServerCookiesGetOrGenerateUserIdentity,
} from '../../../ad-server'
import { useGlooApi } from '../../../api'
import { apmCaptureError } from '../../../apm'
import {
  cookiesGetOrGenerateRecommendationSessionId,
  cookiesIsUserConsentForAnalyticsGranted,
} from '../../../cookies'
import { useExpaContext } from '../../../expa'
import {
  FEATURE_TOGGLE_AD_SERVER,
  useFeatureToggle,
} from '../../../feature-toggles'
import { useGlobalConfigContext } from '../../../global-config'
import { useUdpTrackingBuildMetadata } from '../../../udp-tracking'
import {
  type RecommendationServiceType,
  useRecommendationConfigContext,
} from '../../config'
import { useRecommendationExperimentServiceId } from '../../recommendation-experiments'

import { recommendationBuildRequestParams } from './recommendationBuildRequestParams'
import { recommendationFetchService } from './recommendationFetchService'

type RecommendationFetchServiceParams = {
  cid?: string
  expa?: string
  pid?: string
  plist?: string
  prodrec?: boolean
  searchterm?: string
  sellerid?: string
  serviceId: RecommendationServiceType
}

const RECOMMENDATION_FETCH_SERVICE_SWR_KEY = 'useSwrFetchRecommendationService'

const productListGetFirstProductId = (
  productList: string,
): string | undefined => {
  return productList
    .split(',')
    .map((productId) => productId.trim())
    .find((productId) => productId !== '')
}

type RecommendationFetchServiceResolvedParams = Omit<
  RecommendationFetchServiceParams,
  'serviceId'
> & {
  asuid?: string
  serviceId: string
}

type RecommendationFetchServiceSwrKey = [
  key: typeof RECOMMENDATION_FETCH_SERVICE_SWR_KEY,
  params: RecommendationFetchServiceResolvedParams,
]

const getFetchRecommendationServiceSwrKey = (
  params: RecommendationFetchServiceResolvedParams,
): RecommendationFetchServiceSwrKey => {
  return [RECOMMENDATION_FETCH_SERVICE_SWR_KEY, params]
}

export const useSwrFetchRecommendationService = (
  params?: RecommendationFetchServiceParams,
): {
  data: GkAirProxyServiceWebResponse | undefined
  error: AxiosError | undefined
} => {
  const buildMetadata = useUdpTrackingBuildMetadata()
  const gkAirClient = useGlooApi(GkAirApiFactory)
  const prodrecClient = useGlooApi(ProdrecApiFactory)
  const globalConfig = useGlobalConfigContext()
  const recommendationConfig = useRecommendationConfigContext()
  const isAdServerEnabled = useFeatureToggle(FEATURE_TOGGLE_AD_SERVER)
  const requestServiceId = useRecommendationExperimentServiceId(
    params?.serviceId,
  )

  const isAnalyticsConsentGranted = cookiesIsUserConsentForAnalyticsGranted()
  const expa = useExpaContext()
  const asuid = isAnalyticsConsentGranted
    ? recommendationConfig.asuid
    : undefined

  const getKey = params
    ? getFetchRecommendationServiceSwrKey({
        cid: params.cid,
        ...(expa && { expa }),
        pid: params.pid,
        plist: params.plist,
        ...(params.prodrec && { prodrec: params.prodrec }),
        searchterm: params.searchterm,
        sellerid: params.sellerid,
        serviceId: requestServiceId ?? params.serviceId,
        ...(isAnalyticsConsentGranted && asuid && { asuid }),
      })
    : null

  const fetcher = async (
    fetcherParams: RecommendationFetchServiceSwrKey,
  ): Promise<GkAirProxyServiceWebResponse> => {
    const [, swrParams] = fetcherParams
    const { cid, prodrec, searchterm, sellerid, serviceId } = swrParams

    const plist = swrParams.plist ?? ''
    const pid =
      productListGetFirstProductId(swrParams.pid ?? '') ??
      productListGetFirstProductId(plist) ??
      ''

    let adServerData: AdServerData | null = null

    const { _targeting: targeting } = globalThis

    if (isAdServerEnabled && targeting && 'context' in targeting) {
      const { browserIdConversion, browserIdTargeting } =
        adServerCookiesGetOrGenerateUserIdentity()

      adServerData = {
        ...targeting,
        eventIds: [AD_SERVER_VISIBLE_EVENT_TYPE],
        user: {
          asbidc: browserIdConversion,
          asbidt: browserIdTargeting,
          asuid,
        },
      }
    }

    const requestParams: GkAirApiGetGkAirRecommendationsWebRequest =
      recommendationBuildRequestParams({
        adServerData,
        asuid: swrParams.asuid,
        cid,
        expa: expa ?? undefined,
        globalConfig,
        isAnalyticsConsentGranted,
        pid,
        plist,
        searchterm,
        sellerid,
        serviceId: requestServiceId ?? serviceId,
        sid: cookiesGetOrGenerateRecommendationSessionId(),
      })

    return recommendationFetchService({
      buildMetadata,
      gkAirClient,
      isProdrecCall: Boolean(prodrec),
      prodrecClient,
      requestParams,
    })
  }

  return useSWR<GkAirProxyServiceWebResponse, AxiosError>(getKey, fetcher, {
    onError: (error) => {
      const errorInstance = Error.isError(error)
        ? error
        : new Error('Failed to fetch GK Air recommendations')

      apmCaptureError(errorInstance)
    },
  })
}
